import {
  AppBar as AppBarMui,
  Box,
  ButtonBase,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProps } from "../../../models/theme";
import PortalContext from "../../../context/portalContext";
import { useQueryGroupTheme, useQueryLogo } from "../../../api/queries";
import { getCorporateType, getStorageToken } from "../../../utils";
import { getContrastTextColor } from "../../../utils/colorManager";
import BrandMark from "../../brandMark";

interface HeaderProps {
  theme: ThemeProps;
}

const LANGUAGES = ["en", "fr"] as const;

/**
 * The portal header, per the approved click dummy: a 64px bar in the tenant's
 * primary with a 4px stripe of its secondary along the bottom, the identity on
 * the left and the language as two pills on the right.
 *
 * The identity is the tenant's own logo when one is configured. When none is,
 * it is the tenant's initials in a colour derived from those initials -- the
 * dummy's tile treatment, and better than the empty space a missing logo used
 * to leave. On the bare address there is no tenant, so the mark is the
 * portal's own (SC) beside its name.
 */
const AppBar = ({ theme }: HeaderProps) => {
  const { t, i18n } = useTranslation();
  const token = getStorageToken();
  const groupType = getCorporateType(theme?.url_type);

  // Read directly rather than through usePortal, which throws when there is no
  // provider: the header is chrome and must still draw on a page mounted
  // outside the portal tree (see the app-level tests).
  const portal = useContext(PortalContext);
  const tenantName = portal?.scope?.label ?? "";

  const { data: dataCorporate } = useQueryGroupTheme({
    id: theme?.id,
    groupType: getCorporateType(theme?.url_type),
    // LANDING owns no tenant, and the default theme carries id "0" with that
    // scope -- truthy, so this fired GET /theme/0/corporate-group and always
    // 404ed. It ran on the platform front door, which has no tenant at all,
    // and on every tenant page in the window before its own theme resolved.
    // The shared read refuses the placeholder too; this stays as a safeguard.
    enable: !!theme?.id && theme?.url_type !== "LANDING",
    authorisation: token || "",
  });

  const corporateId = dataCorporate?.id || "";

  const { data } = useQueryLogo({
    groupType,
    corporateId,
    enabled: !!token && !!corporateId,
    authorisation: token || "",
  });

  // One object URL per logo, revoked when it is replaced. Built in the render
  // body it was a new URL on every render that nothing ever released.
  const logoUrl = useMemo(() => (data ? URL.createObjectURL(data) : ""), [data]);
  useEffect(() => {
    if (!logoUrl) return;
    return () => URL.revokeObjectURL(logoUrl);
  }, [logoUrl]);

  const onPrimary = getContrastTextColor(theme.color_primary);
  const language = (i18n.language || "en").slice(0, 2);

  return (
    <AppBarMui
      position="static"
      sx={{
        backgroundColor: theme.color_primary,
        color: onPrimary,
        // The design's line under the header is the tenant's secondary colour.
        borderBottom: `4px solid ${theme.color_secondary}`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1040,
          mx: "auto",
          height: 64,
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          pb: "4px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
          <BrandMark
            name={tenantName || t("brand.name")}
            logoUrl={logoUrl}
            brand={!tenantName}
            announce={!!tenantName}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Bricolage Grotesque", ui-sans-serif, system-ui, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1.15,
                color: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t("brand.name")}
            </Typography>
            <Typography
              component="span"
              sx={{ display: "block", fontSize: 12, lineHeight: 1.15, opacity: 0.85 }}
            >
              {t("brand.by")}
            </Typography>
          </Box>
        </Box>

        <Box
          role="group"
          aria-label={t("language.label")}
          sx={{
            display: "inline-flex",
            flex: "none",
            borderRadius: "999px",
            bgcolor: "rgba(255,255,255,0.14)",
            p: "3px",
          }}
        >
          {LANGUAGES.map(code => {
            const selected = language === code;

            return (
              <ButtonBase
                key={code}
                type="button"
                aria-pressed={selected}
                onClick={() => i18n.changeLanguage(code)}
                sx={{
                  borderRadius: "999px",
                  px: 1.25,
                  py: 0.625,
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: selected ? "text.primary" : "inherit",
                  bgcolor: selected ? "background.paper" : "transparent",
                }}
              >
                {code.toUpperCase()}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    </AppBarMui>
  );
};

export default AppBar;
